import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { TableColumn, TableTypeNames } from './column.service';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { LocalService } from './storage/local.service';
import { UserPreferencesApiService } from './user-preferences-api.service';

/**
 * SD-6481 — backend-backed column preferences for the v2-layout shared
 * `app-table`. Replaces the static, localStorage-only `ColumnService` for v2
 * tables only; legacy app-layout consumers keep using `ColumnService` until
 * migrated in follow-up tickets.
 *
 * Stores the user's visible column keys as a plain `string[]` under
 * `columns.<tableId>` via {@link UserPreferencesApiService}. The stored value
 * intentionally mirrors the legacy shape so the one-time migration is a
 * straight copy. Column schema reconciliation (drop removed columns, surface
 * new ones via the picker) lives in the table component — the backend just
 * stores the last-known selection.
 */
@Injectable({
    providedIn: 'root',
})
export class TableColumnPreferenceService
{
    /** Matches the legacy `ColumnService` localStorage domain. */
    private static readonly LEGACY_DOMAIN = 'payhoa-columns';

    constructor(private _api: UserPreferencesApiService) {}

    /**
     * Visible column keys for a table, or null when the user has no override
     * (table falls back to system defaults from `columnMap`). Lazily migrates
     * a pre-existing legacy localStorage entry to the backend on first read.
     */
    read(table: TableTypeNames): string[] | null {
        const stored = this._api.get<string[]>(this._key(table));
        if (stored !== null) {
            return stored;
        }

        return this._migrateLegacy(table);
    }

    /**
     * Reactive read of a table's stored visible column keys. Emits the
     * current cached value and re-emits when it changes (e.g. when the
     * authenticated shell's bulk GET resolves on a fresh device).
     */
    watch(table: TableTypeNames): Observable<string[] | null> {
        return this._api.watch<string[]>(this._key(table));
    }

    /**
     * Stage the visible columns locally (instant cache + localStorage mirror
     * write, no network). The picker stays open for multi-select, so a backend
     * PUT per toggle would be chatty; callers stage on each toggle and commit
     * once via {@link persist} when the picker closes.
     */
    store(table: TableTypeNames, columns: TableColumn[]): void {
        this._api.setLocal(this._key(table), columns.map((column) => column.key));
    }

    /** Commit the staged selection to the backend (a single PUT). */
    persist(table: TableTypeNames): void {
        this._api.flushKey(this._key(table));
    }

    /** Clear the override, reverting the table to system defaults. */
    clear(table: TableTypeNames): void {
        this._api.remove(this._key(table));
    }

    private _key(table: TableTypeNames): string {
        return `columns.${table}`;
    }

    /**
     * One-time-per-table migration: if the legacy `payhoa-columns-{orgId}`
     * blob still holds this table's selection, copy it to the backend and
     * remove only that entry — legacy app-layout tables that share the blob
     * keep their own entries untouched.
     */
    private _migrateLegacy(table: TableTypeNames): string[] | null {
        const orgId = LegFiJwtService.read()?.orgId ?? null;
        if (orgId === null) {
            return null;
        }

        const legacyKey = `${TableColumnPreferenceService.LEGACY_DOMAIN}-${orgId}`;
        const raw = LocalService.get(legacyKey);
        if (raw === null || raw === '') {
            return null;
        }

        let blob: Partial<Record<TableTypeNames, string[]>>;
        try {
            blob = JSON.parse(raw) as Partial<Record<TableTypeNames, string[]>>;
        } catch {
            return null;
        }

        const legacyValue = blob?.[table];
        if (!Array.isArray(legacyValue)) {
            return null;
        }

        // Push to backend (immediate — a deliberate one-time action) and warm
        // the cache so subsequent reads hit it.
        this._api.set(this._key(table), legacyValue);

        // Drop only this table's entry from the shared legacy blob.
        delete blob[table];
        if (Object.keys(blob).length === 0) {
            LocalService.destroy(legacyKey);
        } else {
            LocalService.set(legacyKey, JSON.stringify(blob));
        }

        return legacyValue;
    }
}
